import { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  format,
  addMonths,
  subMonths,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  isSameMonth,
  isSameDay,
  parseISO,
} from "date-fns";
import { fr } from "date-fns/locale";
import {
  Lock,
  LogOut,
  Calendar as CalendarIcon,
  List as ListIcon,
  CalendarOff,
  ChevronLeft,
  ChevronRight,
  Mail,
  Phone,
  Trash2,
  CheckCircle2,
  XCircle,
  Clock,
  Loader2,
  Trophy,
  Award,
  Filter,
  Plus,
  Send,
  MessageSquare,
  Inbox,
  KeyRound,
} from "lucide-react";
import { toast } from "sonner";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "aymes_admin_token";

const FORMULA_LABELS = {
  individuelle_classique: "Indiv. — Classique",
  individuelle_licencie: "Indiv. — Licencié",
  u13_senior_classique: "U13/Senior — Classique",
  u13_senior_licencie: "U13/Senior — Licencié",
  n3_pro: "N3 / PRO",
  club: "Collaboration Club",
};

const COACH_LABELS = {
  jeremy_aymes: "Jérémy Aymes",
  yves_aymes: "Yves Aymes",
};

const STATUS_META = {
  pending: { label: "En attente", color: "bg-yellow-500/20 text-yellow-300 border-yellow-500/40" },
  confirmed: { label: "Confirmée", color: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40" },
  cancelled: { label: "Annulée", color: "bg-red-500/20 text-red-300 border-red-500/40" },
  completed: { label: "Terminée", color: "bg-blue-500/20 text-blue-300 border-blue-500/40" },
};

const formatPhone = (p) => (p || "").replace(/\s+/g, "");

// ============ AUTH ============
function LoginScreen({ onLogin }) {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem(TOKEN_KEY, data.access_token);
      toast.success("Connexion réussie");
      onLogin(data.access_token);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Erreur de connexion";
      setError(typeof msg === "string" ? msg : "Mot de passe incorrect");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-black px-4"
      style={{
        backgroundImage:
          "radial-gradient(ellipse at top, rgba(204,255,0,0.08) 0%, transparent 50%), radial-gradient(ellipse at bottom, rgba(204,255,0,0.04) 0%, transparent 50%)",
      }}
      data-testid="admin-login-screen"
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-[#ccff00]/10 border border-[#ccff00]/30 mb-4">
            <Lock className="w-7 h-7 text-[#ccff00]" />
          </div>
          <h1 className="text-3xl font-black tracking-tight text-white">
            AYMES <span className="text-[#ccff00]">/ ADMIN</span>
          </h1>
          <p className="text-sm text-neutral-400 mt-2 uppercase tracking-widest">
            Espace coach — Accès restreint
          </p>
        </div>

        <form
          onSubmit={submit}
          className="bg-neutral-950 border border-neutral-800 rounded-2xl p-8 space-y-6 shadow-2xl"
        >
          <div className="space-y-2">
            <Label htmlFor="admin-password" className="text-neutral-300 text-xs uppercase tracking-widest">
              Mot de passe
            </Label>
            <Input
              id="admin-password"
              data-testid="admin-password-input"
              type="password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoFocus
              className="bg-black border-neutral-800 text-white placeholder:text-neutral-600 focus-visible:ring-[#ccff00]/40 h-12"
            />
          </div>

          {error && (
            <div
              data-testid="admin-login-error"
              className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg px-4 py-3"
            >
              {error}
            </div>
          )}

          <button
            type="submit"
            data-testid="admin-login-submit"
            disabled={loading || !password}
            className="w-full bg-[#ccff00] hover:bg-[#b8e600] disabled:opacity-50 disabled:cursor-not-allowed text-black font-bold uppercase tracking-widest text-sm h-12 rounded-xl transition-all"
          >
            {loading ? (
              <span className="inline-flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" /> Connexion…
              </span>
            ) : (
              "Se connecter"
            )}
          </button>

          <p className="text-center text-xs text-neutral-500">
            Cet espace est réservé à Jérémy Aymes
          </p>
        </form>
      </motion.div>
    </div>
  );
}

// ============ STATS ============
function StatsBar({ stats }) {
  if (!stats) return null;
  const items = [
    { key: "upcoming", label: "À venir", value: stats.upcoming, accent: true },
    { key: "pending", label: "En attente", value: stats.pending },
    { key: "confirmed", label: "Confirmées", value: stats.confirmed },
    { key: "completed", label: "Terminées", value: stats.completed },
    { key: "cancelled", label: "Annulées", value: stats.cancelled },
    { key: "total", label: "Total", value: stats.total },
  ];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="admin-stats-bar">
      {items.map((it) => (
        <div
          key={it.key}
          data-testid={`admin-stat-${it.key}`}
          className={`rounded-xl border p-4 ${
            it.accent
              ? "bg-[#ccff00]/5 border-[#ccff00]/30"
              : "bg-neutral-950 border-neutral-800"
          }`}
        >
          <div className="text-[10px] uppercase tracking-widest text-neutral-400 font-bold">
            {it.label}
          </div>
          <div
            className={`text-3xl font-black mt-1 ${
              it.accent ? "text-[#ccff00]" : "text-white"
            }`}
          >
            {it.value ?? 0}
          </div>
        </div>
      ))}
    </div>
  );
}

// ============ WEEKLY PULSE ============
function WeeklyPulse({ stats }) {
  const week = stats?.week;
  if (!week) return null;
  const weekLabel = (() => {
    try {
      const start = parseISO(week.start);
      const end = parseISO(week.end);
      return `Semaine du ${format(start, "d MMM", { locale: fr })} au ${format(end, "d MMM", { locale: fr })}`;
    } catch {
      return "Cette semaine";
    }
  })();
  return (
    <div
      data-testid="admin-weekly-pulse"
      className="relative overflow-hidden rounded-2xl border border-[#ccff00]/20 bg-gradient-to-br from-[#ccff00]/[0.08] via-black to-black p-5"
    >
      <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full bg-[#ccff00]/10 blur-3xl pointer-events-none" />
      <div className="relative flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-widest text-[#ccff00] font-bold">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#ccff00] animate-pulse" />
            Pouls de la semaine
          </div>
          <div className="text-xs text-neutral-500 mt-1 capitalize">{weekLabel}</div>
        </div>
        <div className="flex items-center gap-6 sm:gap-8">
          <div data-testid="admin-week-bookings" className="text-left">
            <div className="text-3xl sm:text-4xl font-black text-[#ccff00] leading-none tabular-nums">
              {week.bookings ?? 0}
            </div>
            <div className="text-[10px] uppercase tracking-widest text-neutral-400 font-bold mt-1">
              Résa{week.bookings > 1 ? "s" : ""}
            </div>
          </div>
          <div className="w-px h-10 bg-neutral-800" />
          <div data-testid="admin-week-messages" className="text-left">
            <div className="text-3xl sm:text-4xl font-black text-white leading-none tabular-nums">
              {week.messages ?? 0}
            </div>
            <div className="text-[10px] uppercase tracking-widest text-neutral-400 font-bold mt-1">
              Message{week.messages > 1 ? "s" : ""}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============ CALENDAR VIEW ============
function CalendarView({ bookings, blockedSlots, onDayClick }) {
  const [currentMonth, setCurrentMonth] = useState(new Date());

  const days = useMemo(() => {
    const start = startOfWeek(startOfMonth(currentMonth), { weekStartsOn: 1 });
    const end = endOfWeek(endOfMonth(currentMonth), { weekStartsOn: 1 });
    return eachDayOfInterval({ start, end });
  }, [currentMonth]);

  const bookingsByDay = useMemo(() => {
    const map = {};
    for (const b of bookings) {
      if (b.status === "cancelled") continue;
      map[b.preferred_date] = (map[b.preferred_date] || 0) + 1;
    }
    return map;
  }, [bookings]);

  const blockedByDay = useMemo(() => {
    const map = {};
    for (const b of blockedSlots) {
      map[b.date] = map[b.date] || { full: false, slots: 0 };
      if (!b.slot_time) map[b.date].full = true;
      else map[b.date].slots += 1;
    }
    return map;
  }, [blockedSlots]);

  return (
    <div className="bg-neutral-950 border border-neutral-800 rounded-2xl p-4 sm:p-6" data-testid="admin-calendar-view">
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
          data-testid="admin-cal-prev"
          className="p-2 rounded-lg hover:bg-neutral-800 text-neutral-300"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <h3 className="text-lg sm:text-xl font-black uppercase tracking-wide text-white">
          {format(currentMonth, "MMMM yyyy", { locale: fr })}
        </h3>
        <button
          onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
          data-testid="admin-cal-next"
          className="p-2 rounded-lg hover:bg-neutral-800 text-neutral-300"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>

      <div className="grid grid-cols-7 gap-2 mb-2">
        {["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"].map((d) => (
          <div key={d} className="text-center text-[10px] uppercase tracking-widest text-neutral-500 font-bold py-2">
            {d}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-2">
        {days.map((d) => {
          const dateKey = format(d, "yyyy-MM-dd");
          const count = bookingsByDay[dateKey] || 0;
          const blocked = blockedByDay[dateKey];
          const inMonth = isSameMonth(d, currentMonth);
          const isToday = isSameDay(d, new Date());
          const hasActivity = count > 0 || blocked;
          return (
            <button
              key={dateKey}
              onClick={() => onDayClick(dateKey)}
              data-testid={`admin-cal-day-${dateKey}`}
              className={`relative aspect-square rounded-lg border text-left p-1.5 sm:p-2 transition-all ${
                inMonth
                  ? "bg-black border-neutral-800 hover:border-[#ccff00]/40"
                  : "bg-black/40 border-neutral-900 text-neutral-700"
              } ${isToday ? "ring-1 ring-[#ccff00]/50" : ""} ${
                hasActivity && inMonth ? "border-[#ccff00]/30" : ""
              }`}
            >
              <div
                className={`text-xs sm:text-sm font-bold ${
                  inMonth ? (isToday ? "text-[#ccff00]" : "text-white") : "text-neutral-700"
                }`}
              >
                {format(d, "d")}
              </div>
              {inMonth && count > 0 && (
                <div className="absolute bottom-1 left-1 right-1 flex items-center gap-1">
                  <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-[#ccff00] text-black text-[10px] font-black">
                    {count}
                  </span>
                </div>
              )}
              {inMonth && blocked?.full && (
                <div className="absolute top-1 right-1">
                  <CalendarOff className="w-3 h-3 text-red-400" />
                </div>
              )}
              {inMonth && !blocked?.full && blocked?.slots > 0 && (
                <div className="absolute top-1 right-1 w-1.5 h-1.5 rounded-full bg-orange-400" />
              )}
            </button>
          );
        })}
      </div>

      <div className="flex flex-wrap items-center gap-4 mt-6 text-[11px] text-neutral-400 uppercase tracking-wider">
        <span className="inline-flex items-center gap-2">
          <span className="inline-block w-4 h-4 rounded-full bg-[#ccff00]" /> Réservations
        </span>
        <span className="inline-flex items-center gap-2">
          <CalendarOff className="w-4 h-4 text-red-400" /> Jour bloqué
        </span>
        <span className="inline-flex items-center gap-2">
          <span className="inline-block w-1.5 h-1.5 rounded-full bg-orange-400" /> Créneau bloqué
        </span>
      </div>
    </div>
  );
}

// ============ DAY DETAIL DIALOG ============
function DayDetailDialog({ dateKey, bookings, blockedSlots, onClose, onSelectBooking, onUnblock }) {
  if (!dateKey) return null;
  const items = bookings.filter((b) => b.preferred_date === dateKey);
  const blocks = blockedSlots.filter((b) => b.date === dateKey);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-neutral-950 border-neutral-800 text-white max-w-2xl" data-testid="admin-day-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl font-black uppercase tracking-wide">
            {format(parseISO(dateKey), "EEEE d MMMM yyyy", { locale: fr })}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 max-h-[60vh] overflow-y-auto">
          {blocks.length > 0 && (
            <div className="space-y-2">
              <div className="text-xs uppercase tracking-widest text-neutral-400 font-bold">Bloqués</div>
              {blocks.map((b) => (
                <div
                  key={b.id}
                  className="flex items-center justify-between bg-red-500/5 border border-red-500/20 rounded-lg px-3 py-2"
                >
                  <div className="flex items-center gap-2">
                    <CalendarOff className="w-4 h-4 text-red-400" />
                    <span className="text-sm">
                      {b.slot_time ? `Créneau ${b.slot_time}` : "Journée entière"}
                    </span>
                    {b.reason && <span className="text-xs text-neutral-500">— {b.reason}</span>}
                  </div>
                  <button
                    onClick={() => onUnblock(b.id)}
                    data-testid={`admin-unblock-${b.id}`}
                    className="text-red-400 hover:text-red-300 text-xs uppercase tracking-wider"
                  >
                    Débloquer
                  </button>
                </div>
              ))}
            </div>
          )}
          {items.length === 0 && blocks.length === 0 && (
            <div className="text-center text-neutral-500 py-8 text-sm">Aucune activité ce jour</div>
          )}
          {items.length > 0 && (
            <div className="space-y-2">
              <div className="text-xs uppercase tracking-widest text-neutral-400 font-bold">
                Réservations ({items.length})
              </div>
              {items.map((b) => (
                <button
                  key={b.id}
                  onClick={() => onSelectBooking(b)}
                  data-testid={`admin-day-booking-${b.id}`}
                  className="w-full text-left bg-black border border-neutral-800 rounded-lg p-3 hover:border-[#ccff00]/40 transition"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="font-bold text-white">{b.full_name}</div>
                      <div className="text-xs text-neutral-400 mt-0.5">
                        {b.slot_time} · {FORMULA_LABELS[b.formula] || b.formula} · {COACH_LABELS[b.coach] || b.coach}
                      </div>
                    </div>
                    <span
                      className={`text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded border ${
                        STATUS_META[b.status]?.color || "bg-neutral-700 text-neutral-300"
                      }`}
                    >
                      {STATUS_META[b.status]?.label || b.status}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ============ LIST VIEW ============
function ListView({ bookings, onSelectBooking }) {
  const [statusFilter, setStatusFilter] = useState("all");
  const [coachFilter, setCoachFilter] = useState("all");
  const [search, setSearch] = useState("");

  const filtered = useMemo(() => {
    return bookings.filter((b) => {
      if (statusFilter !== "all" && b.status !== statusFilter) return false;
      if (coachFilter !== "all" && b.coach !== coachFilter) return false;
      if (search) {
        const q = search.toLowerCase();
        if (
          !b.full_name?.toLowerCase().includes(q) &&
          !b.email?.toLowerCase().includes(q) &&
          !b.phone?.toLowerCase().includes(q)
        )
          return false;
      }
      return true;
    });
  }, [bookings, statusFilter, coachFilter, search]);

  return (
    <div className="bg-neutral-950 border border-neutral-800 rounded-2xl p-4 sm:p-6" data-testid="admin-list-view">
      <div className="flex flex-col md:flex-row md:items-center gap-3 mb-6">
        <div className="flex items-center gap-2 text-neutral-400 text-xs uppercase tracking-widest font-bold">
          <Filter className="w-4 h-4" /> Filtres
        </div>
        <Input
          placeholder="Rechercher (nom, email, tél)…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          data-testid="admin-list-search"
          className="bg-black border-neutral-800 text-white placeholder:text-neutral-600 max-w-xs"
        />
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="bg-black border-neutral-800 text-white w-[160px]" data-testid="admin-list-status-filter">
            <SelectValue placeholder="Statut" />
          </SelectTrigger>
          <SelectContent className="bg-neutral-950 border-neutral-800 text-white">
            <SelectItem value="all">Tous les statuts</SelectItem>
            <SelectItem value="pending">En attente</SelectItem>
            <SelectItem value="confirmed">Confirmées</SelectItem>
            <SelectItem value="completed">Terminées</SelectItem>
            <SelectItem value="cancelled">Annulées</SelectItem>
          </SelectContent>
        </Select>
        <Select value={coachFilter} onValueChange={setCoachFilter}>
          <SelectTrigger className="bg-black border-neutral-800 text-white w-[160px]" data-testid="admin-list-coach-filter">
            <SelectValue placeholder="Coach" />
          </SelectTrigger>
          <SelectContent className="bg-neutral-950 border-neutral-800 text-white">
            <SelectItem value="all">Tous les coachs</SelectItem>
            <SelectItem value="jeremy_aymes">Jérémy Aymes</SelectItem>
            <SelectItem value="yves_aymes">Yves Aymes</SelectItem>
          </SelectContent>
        </Select>
        <div className="ml-auto text-xs text-neutral-500 uppercase tracking-widest">
          {filtered.length} résultat{filtered.length > 1 ? "s" : ""}
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-neutral-800 text-[10px] uppercase tracking-widest text-neutral-500">
              <th className="text-left py-3 px-2 font-bold">Date / Créneau</th>
              <th className="text-left py-3 px-2 font-bold">Client</th>
              <th className="text-left py-3 px-2 font-bold hidden md:table-cell">Formule</th>
              <th className="text-left py-3 px-2 font-bold hidden lg:table-cell">Coach</th>
              <th className="text-left py-3 px-2 font-bold">Statut</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr>
                <td colSpan={5} className="py-12 text-center text-neutral-500">
                  Aucune réservation
                </td>
              </tr>
            )}
            {filtered.map((b) => (
              <tr
                key={b.id}
                onClick={() => onSelectBooking(b)}
                data-testid={`admin-list-row-${b.id}`}
                className="border-b border-neutral-900 hover:bg-neutral-900/40 cursor-pointer transition"
              >
                <td className="py-3 px-2">
                  <div className="font-bold text-white">
                    {format(parseISO(b.preferred_date), "d MMM yyyy", { locale: fr })}
                  </div>
                  <div className="text-xs text-neutral-400">{b.slot_time}</div>
                </td>
                <td className="py-3 px-2">
                  <div className="font-medium text-white">{b.full_name}</div>
                  <div className="text-xs text-neutral-500">{b.email}</div>
                </td>
                <td className="py-3 px-2 hidden md:table-cell text-neutral-300">
                  {FORMULA_LABELS[b.formula] || b.formula}
                </td>
                <td className="py-3 px-2 hidden lg:table-cell text-neutral-300">
                  {COACH_LABELS[b.coach] || b.coach}
                </td>
                <td className="py-3 px-2">
                  <span
                    className={`inline-block text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded border ${
                      STATUS_META[b.status]?.color || "bg-neutral-700 text-neutral-300"
                    }`}
                  >
                    {STATUS_META[b.status]?.label || b.status}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ============ BOOKING DETAIL DIALOG ============
function BookingDetailDialog({ booking, onClose, onUpdateStatus, onDelete, onResendEmail }) {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [resending, setResending] = useState(false);
  if (!booking) return null;

  const tarifLabel = booking.tarif === "licencie" ? "Licencié (−30%)" : "Classique";

  const handleResend = async () => {
    setResending(true);
    try {
      await onResendEmail(booking.id);
    } finally {
      setResending(false);
    }
  };

  return (
    <>
      <Dialog open onOpenChange={(o) => !o && onClose()}>
        <DialogContent className="bg-neutral-950 border-neutral-800 text-white max-w-xl" data-testid="admin-booking-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-black uppercase tracking-wide">
              {booking.full_name}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="bg-black border border-neutral-800 rounded-lg p-3">
                <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-bold">Date</div>
                <div className="font-bold mt-1">
                  {format(parseISO(booking.preferred_date), "EEEE d MMMM yyyy", { locale: fr })}
                </div>
              </div>
              <div className="bg-black border border-neutral-800 rounded-lg p-3">
                <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-bold">Créneau</div>
                <div className="font-bold mt-1 inline-flex items-center gap-2">
                  <Clock className="w-4 h-4 text-[#ccff00]" /> {booking.slot_time}
                </div>
              </div>
            </div>

            <div className="bg-black border border-neutral-800 rounded-lg p-4 space-y-2">
              <div className="flex items-center gap-3">
                <Mail className="w-4 h-4 text-neutral-400" />
                <a href={`mailto:${booking.email}`} className="text-sm hover:text-[#ccff00]">
                  {booking.email}
                </a>
              </div>
              <div className="flex items-center gap-3">
                <Phone className="w-4 h-4 text-neutral-400" />
                <a href={`tel:${formatPhone(booking.phone)}`} className="text-sm hover:text-[#ccff00]">
                  {booking.phone}
                </a>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 text-xs">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-bold mb-1">Formule</div>
                <div className="text-neutral-200">{FORMULA_LABELS[booking.formula] || booking.formula}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-bold mb-1">Tarif</div>
                <div className="text-neutral-200">{tarifLabel}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-bold mb-1">Coach</div>
                <div className="text-neutral-200 inline-flex items-center gap-1">
                  {booking.coach === "jeremy_aymes" ? <Trophy className="w-3 h-3" /> : <Award className="w-3 h-3" />}
                  {COACH_LABELS[booking.coach] || booking.coach}
                </div>
              </div>
              {booking.age_category && (
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-bold mb-1">Catégorie</div>
                  <div className="text-neutral-200">{booking.age_category}</div>
                </div>
              )}
              {booking.level && (
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-bold mb-1">Niveau</div>
                  <div className="text-neutral-200">{booking.level}</div>
                </div>
              )}
            </div>

            {booking.message && (
              <div>
                <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-bold mb-1">Message</div>
                <div className="text-sm text-neutral-300 bg-black border border-neutral-800 rounded-lg p-3 whitespace-pre-wrap">
                  {booking.message}
                </div>
              </div>
            )}

            <div className="flex items-center justify-between pt-2 border-t border-neutral-800">
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-bold">Statut actuel</div>
              <span
                className={`inline-block text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded border ${
                  STATUS_META[booking.status]?.color
                }`}
              >
                {STATUS_META[booking.status]?.label || booking.status}
              </span>
            </div>
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2 mt-4">
            <div className="flex flex-wrap gap-2 flex-1">
              {booking.status !== "confirmed" && (
                <button
                  onClick={() => onUpdateStatus(booking.id, "confirmed")}
                  data-testid="admin-booking-confirm"
                  className="inline-flex items-center gap-1 bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-300 border border-emerald-500/40 px-3 py-2 rounded-lg text-xs uppercase tracking-widest font-bold"
                >
                  <CheckCircle2 className="w-4 h-4" /> Confirmer
                </button>
              )}
              {booking.status !== "completed" && (
                <button
                  onClick={() => onUpdateStatus(booking.id, "completed")}
                  data-testid="admin-booking-complete"
                  className="inline-flex items-center gap-1 bg-blue-500/15 hover:bg-blue-500/25 text-blue-300 border border-blue-500/40 px-3 py-2 rounded-lg text-xs uppercase tracking-widest font-bold"
                >
                  <CheckCircle2 className="w-4 h-4" /> Terminée
                </button>
              )}
              {booking.status !== "cancelled" && (
                <button
                  onClick={() => onUpdateStatus(booking.id, "cancelled")}
                  data-testid="admin-booking-cancel"
                  className="inline-flex items-center gap-1 bg-red-500/15 hover:bg-red-500/25 text-red-300 border border-red-500/40 px-3 py-2 rounded-lg text-xs uppercase tracking-widest font-bold"
                >
                  <XCircle className="w-4 h-4" /> Annuler
                </button>
              )}
              {booking.status !== "pending" && (
                <button
                  onClick={() => onUpdateStatus(booking.id, "pending")}
                  data-testid="admin-booking-pending"
                  className="inline-flex items-center gap-1 bg-yellow-500/15 hover:bg-yellow-500/25 text-yellow-300 border border-yellow-500/40 px-3 py-2 rounded-lg text-xs uppercase tracking-widest font-bold"
                >
                  <Clock className="w-4 h-4" /> En attente
                </button>
              )}
              <button
                onClick={handleResend}
                disabled={resending}
                data-testid="admin-booking-resend-email"
                className="inline-flex items-center gap-1 bg-[#ccff00]/10 hover:bg-[#ccff00]/20 text-[#ccff00] border border-[#ccff00]/40 px-3 py-2 rounded-lg text-xs uppercase tracking-widest font-bold disabled:opacity-50"
              >
                {resending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Renvoyer email
              </button>
            </div>
            <button
              onClick={() => setConfirmDelete(true)}
              data-testid="admin-booking-delete"
              className="inline-flex items-center gap-1 bg-black hover:bg-red-500/10 text-red-400 border border-red-500/40 px-3 py-2 rounded-lg text-xs uppercase tracking-widest font-bold"
            >
              <Trash2 className="w-4 h-4" /> Supprimer
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent className="bg-neutral-950 border-neutral-800 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer la réservation ?</AlertDialogTitle>
            <AlertDialogDescription className="text-neutral-400">
              Cette action est définitive. La réservation de <strong>{booking.full_name}</strong> sera supprimée.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-neutral-800 border-neutral-700 text-white hover:bg-neutral-700" data-testid="admin-delete-cancel">
              Annuler
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                onDelete(booking.id);
                setConfirmDelete(false);
              }}
              data-testid="admin-delete-confirm"
              className="bg-red-500 hover:bg-red-600 text-white"
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

// ============ BLOCKED SLOTS VIEW ============
function BlockedSlotsView({ blockedSlots, onCreate, onDelete }) {
  const [date, setDate] = useState("");
  const [slotTime, setSlotTime] = useState("full"); // 'full' or "HH:MM"
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);

  const sortedBlockedSlots = useMemo(
    () => blockedSlots.slice().sort((a, b) => a.date.localeCompare(b.date)),
    [blockedSlots]
  );

  const submit = async (e) => {
    e.preventDefault();
    if (!date) {
      toast.error("Sélectionne une date");
      return;
    }
    setLoading(true);
    try {
      await onCreate({
        date,
        slot_time: slotTime === "full" ? null : slotTime,
        reason: reason.trim(),
      });
      setDate("");
      setSlotTime("full");
      setReason("");
    } finally {
      setLoading(false);
    }
  };

  const today = new Date().toISOString().slice(0, 10);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="admin-blocked-view">
      <form
        onSubmit={submit}
        className="bg-neutral-950 border border-neutral-800 rounded-2xl p-6 space-y-4"
      >
        <h3 className="text-sm uppercase tracking-widest text-neutral-400 font-bold flex items-center gap-2">
          <Plus className="w-4 h-4 text-[#ccff00]" /> Bloquer un créneau
        </h3>

        <div>
          <Label className="text-xs uppercase tracking-widest text-neutral-400">Date</Label>
          <Input
            type="date"
            value={date}
            min={today}
            onChange={(e) => setDate(e.target.value)}
            data-testid="admin-block-date"
            className="bg-black border-neutral-800 text-white mt-1"
            required
          />
        </div>

        <div>
          <Label className="text-xs uppercase tracking-widest text-neutral-400">Créneau</Label>
          <Select value={slotTime} onValueChange={setSlotTime}>
            <SelectTrigger className="bg-black border-neutral-800 text-white mt-1" data-testid="admin-block-slot">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-neutral-950 border-neutral-800 text-white">
              <SelectItem value="full">Journée entière</SelectItem>
              <SelectItem value="17:00">17:00</SelectItem>
              <SelectItem value="18:00">18:00</SelectItem>
              <SelectItem value="19:00">19:00</SelectItem>
              <SelectItem value="20:00">20:00</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label className="text-xs uppercase tracking-widest text-neutral-400">Motif (optionnel)</Label>
          <Input
            placeholder="Vacances, indisponibilité…"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            data-testid="admin-block-reason"
            className="bg-black border-neutral-800 text-white mt-1"
          />
        </div>

        <button
          type="submit"
          disabled={loading || !date}
          data-testid="admin-block-submit"
          className="w-full bg-[#ccff00] hover:bg-[#b8e600] disabled:opacity-50 text-black font-bold uppercase tracking-widest text-sm h-11 rounded-xl"
        >
          {loading ? "Blocage…" : "Bloquer"}
        </button>
      </form>

      <div className="bg-neutral-950 border border-neutral-800 rounded-2xl p-6">
        <h3 className="text-sm uppercase tracking-widest text-neutral-400 font-bold mb-4">
          Créneaux bloqués ({blockedSlots.length})
        </h3>
        <div className="space-y-2 max-h-[400px] overflow-y-auto">
          {blockedSlots.length === 0 && (
            <div className="text-center text-neutral-500 py-12 text-sm">Aucun créneau bloqué</div>
          )}
          {sortedBlockedSlots
            .map((b) => (
              <div
                key={b.id}
                data-testid={`admin-blocked-item-${b.id}`}
                className="flex items-center justify-between bg-black border border-neutral-800 rounded-lg px-3 py-2"
              >
                <div>
                  <div className="text-sm font-bold text-white">
                    {format(parseISO(b.date), "d MMM yyyy", { locale: fr })}
                    <span className="text-neutral-400 font-normal ml-2">
                      · {b.slot_time ? `${b.slot_time}` : "Journée entière"}
                    </span>
                  </div>
                  {b.reason && <div className="text-xs text-neutral-500 mt-0.5">{b.reason}</div>}
                </div>
                <button
                  onClick={() => onDelete(b.id)}
                  data-testid={`admin-blocked-delete-${b.id}`}
                  className="text-red-400 hover:text-red-300 p-1.5 rounded"
                  title="Débloquer"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
}

// ============ MESSAGES VIEW ============
function MessagesView({ messages, onDelete }) {
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);

  const filtered = useMemo(() => {
    if (!search) return messages;
    const q = search.toLowerCase();
    return messages.filter(
      (m) =>
        m.full_name?.toLowerCase().includes(q) ||
        m.email?.toLowerCase().includes(q) ||
        m.subject?.toLowerCase().includes(q) ||
        m.message?.toLowerCase().includes(q)
    );
  }, [messages, search]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-4" data-testid="admin-messages-view">
      {/* List column */}
      <div className="lg:col-span-2 bg-neutral-950 border border-neutral-800 rounded-2xl p-4 flex flex-col max-h-[70vh]">
        <div className="flex items-center gap-2 mb-3">
          <Inbox className="w-4 h-4 text-[#ccff00]" />
          <div className="text-xs uppercase tracking-widest text-neutral-400 font-bold">
            Boîte de messages ({messages.length})
          </div>
        </div>
        <Input
          placeholder="Rechercher…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          data-testid="admin-messages-search"
          className="bg-black border-neutral-800 text-white placeholder:text-neutral-600 mb-3"
        />
        <div className="overflow-y-auto flex-1 space-y-2 pr-1">
          {filtered.length === 0 && (
            <div className="text-center text-neutral-500 py-8 text-sm">Aucun message</div>
          )}
          {filtered.map((m) => {
            const isActive = selected?.id === m.id;
            const created = m.created_at ? new Date(m.created_at) : null;
            return (
              <button
                key={m.id}
                onClick={() => setSelected(m)}
                data-testid={`admin-message-item-${m.id}`}
                className={`w-full text-left rounded-lg border p-3 transition ${
                  isActive
                    ? "bg-[#ccff00]/5 border-[#ccff00]/40"
                    : "bg-black border-neutral-800 hover:border-neutral-700"
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="font-bold text-white text-sm truncate">{m.full_name}</div>
                  {created && (
                    <div className="text-[10px] text-neutral-500 whitespace-nowrap">
                      {format(created, "d MMM", { locale: fr })}
                    </div>
                  )}
                </div>
                <div className="text-xs text-neutral-400 mt-0.5 truncate">
                  {m.subject || m.email}
                </div>
                <div className="text-xs text-neutral-500 mt-1 line-clamp-1">{m.message}</div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Detail column */}
      <div className="lg:col-span-3 bg-neutral-950 border border-neutral-800 rounded-2xl p-6 min-h-[70vh]">
        {!selected ? (
          <div className="h-full flex flex-col items-center justify-center text-neutral-600 text-sm">
            <MessageSquare className="w-10 h-10 mb-3 opacity-40" />
            Sélectionne un message pour le lire
          </div>
        ) : (
          <MessageDetail
            message={selected}
            onDelete={async (id) => {
              await onDelete(id);
              setSelected(null);
            }}
          />
        )}
      </div>
    </div>
  );
}

function MessageDetail({ message, onDelete }) {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const phone = message.phone && message.phone.replace(/\s+/g, "");
  return (
    <div data-testid="admin-message-detail" className="flex flex-col h-full">
      <div className="flex-1">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-bold">
              De
            </div>
            <div className="text-lg font-black text-white mt-0.5">{message.full_name}</div>
            <div className="flex flex-wrap items-center gap-3 mt-2 text-sm">
              <a
                href={`mailto:${message.email}`}
                className="inline-flex items-center gap-1 text-[#ccff00] hover:underline"
                data-testid="admin-message-reply-email"
              >
                <Mail className="w-3.5 h-3.5" /> {message.email}
              </a>
              {message.phone && (
                <a
                  href={`tel:${phone}`}
                  className="inline-flex items-center gap-1 text-[#ccff00] hover:underline"
                  data-testid="admin-message-call"
                >
                  <Phone className="w-3.5 h-3.5" /> {message.phone}
                </a>
              )}
            </div>
          </div>
          {message.created_at && (
            <div className="text-right text-[11px] text-neutral-500 whitespace-nowrap">
              {format(new Date(message.created_at), "d MMM yyyy", { locale: fr })}
              <br />
              {format(new Date(message.created_at), "HH:mm", { locale: fr })}
            </div>
          )}
        </div>

        {message.subject && (
          <div className="mt-6">
            <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-bold">
              Sujet
            </div>
            <div className="text-white font-bold mt-1">{message.subject}</div>
          </div>
        )}

        <div className="mt-6">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-bold">
            Message
          </div>
          <div className="mt-2 bg-black border border-neutral-800 rounded-lg p-4 text-sm text-neutral-200 whitespace-pre-wrap leading-relaxed">
            {message.message}
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 pt-4 mt-4 border-t border-neutral-800">
        <a
          href={`mailto:${message.email}?subject=${encodeURIComponent(
            message.subject ? "Re: " + message.subject : "Réponse à ton message"
          )}`}
          data-testid="admin-message-reply-btn"
          className="inline-flex items-center gap-1 bg-[#ccff00] hover:bg-[#b8e600] text-black font-bold uppercase tracking-widest text-xs px-4 py-2 rounded-lg"
        >
          <Send className="w-4 h-4" /> Répondre
        </a>
        <button
          onClick={() => setConfirmDelete(true)}
          data-testid="admin-message-delete-btn"
          className="inline-flex items-center gap-1 bg-black hover:bg-red-500/10 text-red-400 border border-red-500/40 px-4 py-2 rounded-lg text-xs uppercase tracking-widest font-bold ml-auto"
        >
          <Trash2 className="w-4 h-4" /> Supprimer
        </button>
      </div>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent className="bg-neutral-950 border-neutral-800 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer ce message ?</AlertDialogTitle>
            <AlertDialogDescription className="text-neutral-400">
              Cette action est définitive. Le message de <strong>{message.full_name}</strong> sera
              supprimé.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-neutral-800 border-neutral-700 text-white hover:bg-neutral-700">
              Annuler
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                onDelete(message.id);
                setConfirmDelete(false);
              }}
              data-testid="admin-message-delete-confirm"
              className="bg-red-500 hover:bg-red-600 text-white"
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ============ CHANGE PASSWORD DIALOG ============
function ChangePasswordDialog({ open, onClose, token, onSuccess }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const reset = () => {
    setCurrent("");
    setNext("");
    setConfirm("");
    setError("");
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (next.length < 8) {
      setError("Le nouveau mot de passe doit contenir au moins 8 caractères.");
      return;
    }
    if (next !== confirm) {
      setError("La confirmation ne correspond pas au nouveau mot de passe.");
      return;
    }
    if (next === current) {
      setError("Le nouveau mot de passe doit être différent de l'ancien.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(
        `${API}/admin/change-password`,
        { current_password: current, new_password: next },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Mot de passe mis à jour");
      reset();
      onSuccess?.();
      onClose();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Erreur";
      setError(typeof msg === "string" ? msg : "Requête invalide");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { reset(); onClose(); } }}>
      <DialogContent className="bg-neutral-950 border-neutral-800 text-white max-w-md" data-testid="admin-change-password-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl font-black uppercase tracking-wide inline-flex items-center gap-2">
            <KeyRound className="w-5 h-5 text-[#ccff00]" /> Changer le mot de passe
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="text-xs uppercase tracking-widest text-neutral-400">Mot de passe actuel</Label>
            <Input
              type="password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              required
              autoFocus
              data-testid="admin-current-password"
              className="mt-1 bg-black border-neutral-800 text-white h-11"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-neutral-400">Nouveau mot de passe</Label>
            <Input
              type="password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              required
              minLength={8}
              placeholder="Au moins 8 caractères"
              data-testid="admin-new-password"
              className="mt-1 bg-black border-neutral-800 text-white h-11"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-neutral-400">Confirmer le nouveau mot de passe</Label>
            <Input
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
              data-testid="admin-confirm-password"
              className="mt-1 bg-black border-neutral-800 text-white h-11"
            />
          </div>
          {error && (
            <div data-testid="admin-change-password-error" className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2">
              {error}
            </div>
          )}
          <div className="text-[11px] text-neutral-500 bg-black/50 border border-neutral-800 rounded-lg p-3 leading-relaxed">
            💡 Astuce : combine <strong>majuscule + minuscule + chiffre + caractère spécial</strong> et au moins <strong>12 caractères</strong>. Les sessions déjà ouvertes restent valides jusqu&apos;à leur expiration (12h).
          </div>
          <DialogFooter className="gap-2">
            <button
              type="button"
              onClick={() => { reset(); onClose(); }}
              data-testid="admin-change-password-cancel"
              className="bg-neutral-800 hover:bg-neutral-700 text-white text-xs uppercase tracking-widest font-bold px-4 py-2.5 rounded-lg"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={loading || !current || !next || !confirm}
              data-testid="admin-change-password-submit"
              className="bg-[#ccff00] hover:bg-[#b8e600] disabled:opacity-50 text-black text-xs uppercase tracking-widest font-bold px-4 py-2.5 rounded-lg inline-flex items-center gap-2"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />}
              Mettre à jour
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ============ MAIN ADMIN PAGE ============
function Dashboard({ token, onLogout }) {
  const [tab, setTab] = useState("calendar"); // calendar | list | blocked | messages
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [blockedSlots, setBlockedSlots] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);

  const authHeader = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchAll = useCallback(async () => {
    try {
      const [s, b, bl, ms] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers: authHeader }),
        axios.get(`${API}/admin/bookings`, { headers: authHeader }),
        axios.get(`${API}/admin/blocked-slots`, { headers: authHeader }),
        axios.get(`${API}/admin/messages`, { headers: authHeader }),
      ]);
      setStats(s.data);
      setBookings(b.data);
      setBlockedSlots(bl.data);
      setMessages(ms.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        onLogout();
      } else {
        toast.error("Erreur de chargement");
      }
    } finally {
      setLoading(false);
    }
  }, [authHeader, onLogout]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const updateBookingStatus = async (id, status) => {
    try {
      const { data } = await axios.patch(
        `${API}/admin/bookings/${id}`,
        { status },
        { headers: authHeader }
      );
      setBookings((prev) => prev.map((b) => (b.id === id ? data : b)));
      setSelectedBooking(data);
      toast.success(`Statut → ${STATUS_META[status].label}`);
      // Refresh stats
      const s = await axios.get(`${API}/admin/stats`, { headers: authHeader });
      setStats(s.data);
    } catch {
      toast.error("Échec mise à jour");
    }
  };

  const deleteBooking = async (id) => {
    try {
      await axios.delete(`${API}/admin/bookings/${id}`, { headers: authHeader });
      setBookings((prev) => prev.filter((b) => b.id !== id));
      setSelectedBooking(null);
      toast.success("Réservation supprimée");
      const s = await axios.get(`${API}/admin/stats`, { headers: authHeader });
      setStats(s.data);
    } catch {
      toast.error("Échec suppression");
    }
  };

  const createBlock = async (payload) => {
    try {
      const { data } = await axios.post(`${API}/admin/blocked-slots`, payload, {
        headers: authHeader,
      });
      setBlockedSlots((prev) => [...prev, data]);
      toast.success("Créneau bloqué");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Erreur";
      toast.error(typeof msg === "string" ? msg : "Erreur");
    }
  };

  const deleteBlock = async (id) => {
    try {
      await axios.delete(`${API}/admin/blocked-slots/${id}`, { headers: authHeader });
      setBlockedSlots((prev) => prev.filter((b) => b.id !== id));
      toast.success("Créneau débloqué");
    } catch {
      toast.error("Échec");
    }
  };

  const resendEmail = async (id) => {
    try {
      const { data } = await axios.post(
        `${API}/admin/bookings/${id}/resend-email`,
        {},
        { headers: authHeader }
      );
      toast.success(`Email renvoyé à ${data.sent_to}`);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Échec d'envoi";
      toast.error(typeof msg === "string" ? msg : "Échec d'envoi");
    }
  };

  const deleteMessage = async (id) => {
    try {
      await axios.delete(`${API}/admin/messages/${id}`, { headers: authHeader });
      setMessages((prev) => prev.filter((m) => m.id !== id));
      toast.success("Message supprimé");
      const s = await axios.get(`${API}/admin/stats`, { headers: authHeader });
      setStats(s.data);
    } catch {
      toast.error("Échec suppression");
    }
  };

  const handleLogout = () => {
    localStorage.removeItem(TOKEN_KEY);
    onLogout();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#ccff00] animate-spin" />
      </div>
    );
  }

  const tabs = [
    { id: "calendar", label: "Calendrier", icon: CalendarIcon },
    { id: "list", label: "Liste", icon: ListIcon },
    { id: "messages", label: "Messages", icon: Inbox, badge: messages.length },
    { id: "blocked", label: "Bloquer", icon: CalendarOff },
  ];

  return (
    <div className="min-h-screen bg-black text-white" data-testid="admin-dashboard">
      {/* Header */}
      <header className="border-b border-neutral-900 bg-black/80 backdrop-blur sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-lg sm:text-xl font-black tracking-tight">
              AYMES <span className="text-[#ccff00]">/ ADMIN</span>
            </h1>
            <p className="text-[10px] uppercase tracking-widest text-neutral-500">
              Espace coach — Jérémy Aymes
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowPasswordDialog(true)}
              data-testid="admin-change-password-btn"
              title="Changer le mot de passe"
              className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-neutral-400 hover:text-[#ccff00] border border-neutral-800 hover:border-[#ccff00]/40 px-3 py-2 rounded-lg"
            >
              <KeyRound className="w-4 h-4" />
              <span className="hidden sm:inline">Mot de passe</span>
            </button>
            <button
              onClick={handleLogout}
              data-testid="admin-logout"
              className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-neutral-400 hover:text-[#ccff00] border border-neutral-800 hover:border-[#ccff00]/40 px-3 py-2 rounded-lg"
            >
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline">Déconnexion</span>
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        <WeeklyPulse stats={stats} />
        <StatsBar stats={stats} />

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 border-b border-neutral-900 pb-1" data-testid="admin-tabs">
          {tabs.map((t) => {
            const Icon = t.icon;
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                data-testid={`admin-tab-${t.id}`}
                className={`inline-flex items-center gap-2 px-4 py-2.5 text-xs uppercase tracking-widest font-bold rounded-t-lg transition ${
                  active
                    ? "bg-neutral-950 text-[#ccff00] border-x border-t border-neutral-800"
                    : "text-neutral-500 hover:text-white"
                }`}
              >
                <Icon className="w-4 h-4" /> {t.label}
                {t.badge > 0 && (
                  <span
                    data-testid={`admin-tab-badge-${t.id}`}
                    className={`inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-black ${
                      active ? "bg-[#ccff00] text-black" : "bg-neutral-800 text-white"
                    }`}
                  >
                    {t.badge}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={tab}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            {tab === "calendar" && (
              <CalendarView
                bookings={bookings}
                blockedSlots={blockedSlots}
                onDayClick={setSelectedDate}
              />
            )}
            {tab === "list" && (
              <ListView bookings={bookings} onSelectBooking={setSelectedBooking} />
            )}
            {tab === "messages" && (
              <MessagesView messages={messages} onDelete={deleteMessage} />
            )}
            {tab === "blocked" && (
              <BlockedSlotsView
                blockedSlots={blockedSlots}
                onCreate={createBlock}
                onDelete={deleteBlock}
              />
            )}
          </motion.div>
        </AnimatePresence>
      </main>

      {selectedDate && (
        <DayDetailDialog
          dateKey={selectedDate}
          bookings={bookings}
          blockedSlots={blockedSlots}
          onClose={() => setSelectedDate(null)}
          onSelectBooking={(b) => {
            setSelectedDate(null);
            setSelectedBooking(b);
          }}
          onUnblock={async (id) => {
            await deleteBlock(id);
          }}
        />
      )}

      {selectedBooking && (
        <BookingDetailDialog
          booking={selectedBooking}
          onClose={() => setSelectedBooking(null)}
          onUpdateStatus={updateBookingStatus}
          onDelete={deleteBooking}
          onResendEmail={resendEmail}
        />
      )}

      <ChangePasswordDialog
        open={showPasswordDialog}
        onClose={() => setShowPasswordDialog(false)}
        token={token}
      />
    </div>
  );
}

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [checking, setChecking] = useState(!!token);

  useEffect(() => {
    if (!token) {
      setChecking(false);
      return;
    }
    // Validate token
    axios
      .get(`${API}/admin/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(() => setChecking(false))
      .catch(() => {
        localStorage.removeItem(TOKEN_KEY);
        setToken(null);
        setChecking(false);
      });
  }, [token]);

  if (checking) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#ccff00] animate-spin" />
      </div>
    );
  }

  if (!token) {
    return <LoginScreen onLogin={setToken} />;
  }

  return <Dashboard token={token} onLogout={() => setToken(null)} />;
}
